import { ChangeLog } from "@/v1/api/graphql";
import type { IUser } from "@/v1/types";

export type TaxRegistrationStatus =
  | "DRAFT"
  | "READY"
  | "TRANSMITTED"
  | "COMPLETED";

export interface AddressAnswers {
  street?: string;
  zip?: string;
  city?: string;
}

export enum TaxRegistrationActivityType {
  TRADE = "TRADE",
  FREELANCE = "FREELANCE",
}

export enum TaxRegistrationCashAccountingReason {
  TURNOVER_LIMIT = "TURNOVER_LIMIT",
  SECTION_148_AO = "SECTION_148_AO",
  FREELANCE = "FREELANCE",
}

export enum TaxRegistrationEstimatedBalanceType {
  PAYMENT = "PAYMENT",
  REFUND = "REFUND",
}

export enum TaxRegistrationTaxCalculationMethod {
  CASH = "CASH",
  ACCRUAL = "ACCRUAL",
}

/*
 * Everything below belongs to the Kapitalgesellschaft questionnaire (FsE_KapG,
 * SMT-1294). The names and the enum values mirror the stored shape agreed with
 * kontax-backend in SMT-1293, which in turn mirrors the kontax-neric-api
 * contract, so the mapper on the backend is close to an identity. Renaming
 * anything here silently breaks a payload three repositories away.
 */
export enum TaxRegistrationLegalForm {
  GMBH = "GMBH",
  UG = "UG",
}

export enum TaxRegistrationShareholderType {
  NATURAL = "NATURAL",
  LEGAL = "LEGAL",
}

export enum TaxRegistrationAddressType {
  DOMESTIC = "DOMESTIC",
  FOREIGN = "FOREIGN",
}

/** ELSTER's own spelling: GsR and GnR are mixed case on the wire. */
export enum TaxRegistrationRegisterType {
  HRA = "HRA",
  HRB = "HRB",
  GSR = "GsR",
  PR = "PR",
  GNR = "GnR",
  VR = "VR",
}

/**
 * A shareholder or representative address. Domestic uses `zip`, foreign uses
 * `foreignZip` plus `country` (an ELSTER StaatId) and must not carry `zip` at
 * all. `type` absent means domestic.
 */
export interface TaxRegistrationKapgAddress extends AddressAnswers {
  type?: TaxRegistrationAddressType;
  addressExtra?: string;
  foreignZip?: string;
  country?: string;
}

/** Zeilen 68 to 78. NATURAL and LEGAL fields must not be mixed on the wire. */
export interface TaxRegistrationShareholder {
  type?: TaxRegistrationShareholderType;
  subscriberNumber?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  companyName?: string;
  businessActivity?: string;
  address?: TaxRegistrationKapgAddress;
  nominal?: number;
  percentage?: number;
}

/** Zeilen 25 to 30. */
export interface TaxRegistrationRepresentative {
  salutation?: "MS" | "MR" | "NOT_SPECIFIED";
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  address?: TaxRegistrationKapgAddress;
}

/** Zeilen 55 to 57. */
export interface TaxRegistrationCommercialRegister {
  applicationFiled?: boolean;
  applicationDate?: string;
  registered?: boolean;
  registrationDate?: string;
  court?: string;
  registerType?: TaxRegistrationRegisterType;
  registerNumber?: string;
}

export interface TaxRegistrationCompany {
  /** Zeile 2, prefilled from the Mandant's legal name. */
  name?: string;
  /** Zeile 63, follows the Mandant's Rechtsform. */
  legalForm?: TaxRegistrationLegalForm;
  /** Zeilen 4 to 6, domestic only, so no address type. */
  businessAddress?: AddressAnswers;
  /** Zeile 13. */
  activityDescription?: string;
  /** Zeile 14. When true the whole VAT section is absent. */
  vermoegensverwaltend?: boolean;
  /** Zeilen 8 to 10, optional, domestic only. */
  managementAddress?: AddressAnswers;
  /** Zeilen 54 to 57. */
  notarization?: {
    contractDate?: string;
    commercialRegister?: TaxRegistrationCommercialRegister;
  };
  /** Zeile 64. */
  startDate?: string;
  /** Zeilen 66 and 67, whole euros. */
  shareCapital?: number;
  paidInCapital?: number;
}

/** Zeilen 169 to 173. Headcounts are non-negative integers. */
export interface TaxRegistrationPayroll {
  employeesTotal?: number;
  employeesShareholders?: number;
  employeesMinorEmployment?: number;
  paymentStartDate?: string;
  expectedWageTax?: number;
}

export interface TaxRegistrationQuestions {
  taxNumber?: {
    state?: string;
    taxOfficeName?: string;
    taxOfficeCode?: string;
  };
  personal?: {
    salutation?: "MS" | "MR" | "NOT_SPECIFIED";
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    taxId?: string;
    address?: AddressAnswers;
  };
  activity?: {
    type?: TaxRegistrationActivityType;
    startDate?: string;
    description?: string;
    businessAddressSameAsPrivateAddress?: boolean;
    businessAddress?: AddressAnswers;
  };
  /** KapG only, Zeilen 2 to 67. */
  company?: TaxRegistrationCompany;
  /** KapG only, Zeilen 25 to 30, at most 3. */
  representatives?: TaxRegistrationRepresentative[];
  /** KapG only, Zeilen 68 to 78, 1 to 6. */
  shareholders?: TaxRegistrationShareholder[];
  /** KapG only, Zeilen 169 to 173. */
  payroll?: TaxRegistrationPayroll;
  profit?: {
    /** EUN only, Zeilen 102/103. Non-negative in the API schema. */
    expectedCurrentYear?: number;
    expectedFollowingYear?: number;
    /**
     * KapG only, Zeile 165: the Jahresüberschuss of the year of formation and
     * of the following year, in whole euros. Signed, because a Jahresfehlbetrag
     * is a legal answer in FsE_KapG. That is why these are keys of their own
     * rather than the two above (kontax-backend#906, SMT-1293).
     */
    netIncome?: number;
    netIncomeFollowingYear?: number;
  };
  vat?: {
    expectedTurnover?: number;
    expectedTurnoverFollowingYear?: number;
    smallBusiness?: boolean;
    vatIdRequested?: boolean;
    estimatedBalanceAmount?: number;
    estimatedBalanceType?: TaxRegistrationEstimatedBalanceType;
    taxCalculationMethod?: TaxRegistrationTaxCalculationMethod;
    /** EUN only: KapG states its reasons as the two flags below. */
    cashAccountingReason?: TaxRegistrationCashAccountingReason;
    /** KapG Zeile 195, only ever present under Ist-Versteuerung. */
    cashAccountingTurnoverLimit?: boolean;
    /** KapG Zeile 196, only ever present under Ist-Versteuerung. */
    cashAccountingSection148Ao?: boolean;
    /** KapG Zeile 181, mutually exclusive with `smallBusiness`. */
    smallBusinessWaiver?: boolean;
    /** KapG Zeile 183. */
    monthlyFilingPeriod?: boolean;
  };
}

/**
 * One PDF filed with the questionnaire as an ELSTER `Anhang` (Zeile 227,
 * SMT-1341). The metadata is what kontax-backend stored after it read the
 * uploaded object, not what the browser claimed: `size` and `pageCount` are the
 * values the limits were checked against, so the list shows the file as ELSTER
 * will receive it.
 */
export interface TaxRegistrationAttachment {
  id: string;
  fileName: string;
  size: number;
  pageCount: number;
  uploadedAt: string;
}

export interface TaxRegistration {
  id: string;
  userId: number;
  user: IUser;
  status: TaxRegistrationStatus;
  email: string;
  questions: TaxRegistrationQuestions;
  /** KapG only, and always an array on the wire (defaults to `[]`). */
  attachments: TaxRegistrationAttachment[];
  changeLogs?: ChangeLog[];
  submittedDocuments?: TaxRegistrationSubmittedDocument[];
  createdAt: string;
  updatedAt: string;
}

export interface TaxRegistrationSubmittedDocument {
  id: number;
  amount?: number | null;
  period: string;
  year: number;
  uploadedAt?: string | null;
  createdAt?: string;
  isCorrection: boolean;
  transmissionMethod: "AUTOMATIC" | "MANUAL";
  submitterEmail?: string | null;
}

export interface TaxRegistrationFormValues {
  email: string;
  data: TaxRegistrationQuestions;
  businessAddressMode?: "SAME" | "DIFFERENT";
}

export const isTaxRegistrationComplete = (
  values?: Partial<TaxRegistrationFormValues>
): boolean => {
  const taxNumber = values?.data?.taxNumber;
  const personal = values?.data?.personal;
  const activity = values?.data?.activity;
  const profit = values?.data?.profit;
  const vat = values?.data?.vat;
  const hasText = (value?: string): boolean => Boolean(value?.trim());
  const hasNumber = (value?: number | null): boolean => value != null;
  const hasCompleteBusinessAddress =
    activity?.businessAddressSameAsPrivateAddress !== false ||
    (hasText(activity?.businessAddress?.street) &&
      hasText(activity?.businessAddress?.zip) &&
      hasText(activity?.businessAddress?.city));
  const hasRequiredCashAccountingReason =
    vat?.taxCalculationMethod !== TaxRegistrationTaxCalculationMethod.CASH ||
    Boolean(vat.cashAccountingReason);

  return Boolean(
    hasText(values?.email) &&
      hasText(taxNumber?.state) &&
      hasText(taxNumber?.taxOfficeName) &&
      hasText(taxNumber?.taxOfficeCode) &&
      hasText(personal?.firstName) &&
      hasText(personal?.lastName) &&
      hasText(personal?.taxId) &&
      hasText(personal?.address?.street) &&
      hasText(personal?.address?.zip) &&
      hasText(personal?.address?.city) &&
      activity?.type &&
      activity?.startDate &&
      hasText(activity.description) &&
      hasCompleteBusinessAddress &&
      hasNumber(profit?.expectedCurrentYear) &&
      hasNumber(profit?.expectedFollowingYear) &&
      hasNumber(vat?.expectedTurnover) &&
      hasNumber(vat?.expectedTurnoverFollowingYear) &&
      hasNumber(vat?.estimatedBalanceAmount) &&
      hasRequiredCashAccountingReason
  );
};

export const isTaxRegistrationReadOnly = ({
  status,
}: TaxRegistration): boolean =>
  status === "TRANSMITTED" || status === "COMPLETED";
